function safeObject(value, fallback = {}) {
  if (!value) return fallback;

  if (typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed)
        ? parsed
        : fallback;
    } catch {
      return fallback;
    }
  }

  return fallback;
}

export const SOURCE_TYPE_LABELS = {
  flooring: "Flooring",
  wall: "Wall",
  terrace: "Terrace",
  ceiling: "Ceiling",
  painting: "Painting",
  roof: "Roof",
  windows_doors: "Windows / Doors",
  custom: "Custom",
  unknown: "Other",
  merged: "Merged",
};

export function getDefaultSourceLabel(sourceType, rowKind, sourceIndex) {
  const sourceBase = SOURCE_TYPE_LABELS[sourceType] || SOURCE_TYPE_LABELS.unknown;
  const indexLabel =
    Number.isFinite(Number(sourceIndex)) && Number(sourceIndex) >= 0
      ? ` ${Number(sourceIndex) + 1}`
      : "";

  if (sourceType === "windows_doors") {
    return sourceBase;
  }

  if (rowKind === "extra") {
    return `${sourceBase}${indexLabel}`;
  }

  return `${sourceBase}${indexLabel}`;
}

export function getRowQuantity(row) {
  return Number(row?.quantity ?? row?.qty ?? row?.hours ?? 0);
}

export function getRowUnitPrice(row) {
  return Number(
    row?.unitPrice ?? row?.hourlyRate ?? row?.price ?? row?.rate ?? 0
  );
}

export function isManualRow(row) {
  return !!(row?.flags?.manuallyEdited || row?.manuallyEdited);
}

export function isAiAdjusted(row) {
  return !!(row?.flags?.aiAdjusted || row?.aiAdjusted);
}

export function isReviewRow(row) {
  return !!(row?.flags?.reviewRequired || row?.meta?.review);
}

export function normalizeRowMeta(row = {}, rowType = "material", index = 0) {
  const meta = safeObject(row?.meta, {});
  const sourceType = meta.sourceType || "unknown";

  const sourceIndex =
    meta.sourceIndex ??
    meta.roomIndex ??
    meta.wallIndex ??
    meta.terraceIndex ??
    meta.ceilingIndex ??
    meta.paintingIndex ??
    meta.roofIndex ??
    meta.openingIndex ??
    meta.itemIndex ??
    null;

  const sourceId =
    meta.sourceId ||
    (sourceType === "windows_doors" && meta.sourceGroup && sourceIndex !== null
      ? `${meta.sourceGroup}_${Number(sourceIndex) + 1}`
      : sourceIndex !== null
      ? `${sourceType}_${Number(sourceIndex) + 1}`
      : `${sourceType}_${index + 1}`);

  const sourceLabel =
    meta.sourceLabel ||
    getDefaultSourceLabel(sourceType, rowType, sourceIndex ?? index);

  return {
    ...meta,
    sourceType,
    sourceId,
    sourceLabel,
    sourceIndex: sourceIndex ?? index,
    calculator: meta.calculator || sourceType || "unknown",
    pricingMode:
      meta.pricingMode ||
      (isManualRow(row) ? "manual" : isAiAdjusted(row) ? "ai_adjusted" : "ai"),
    review:
      typeof meta.review === "boolean"
        ? meta.review
        : Boolean(row?.needsReview || row?.review || isReviewRow(row)),
    notes: meta.notes || "",
    rowKind: meta.rowKind || rowType,
  };
}

export function rowSignature(row) {
  const label = String(row?.label || row?.name || "")
    .trim()
    .toLowerCase();

  const sourceType = row?.meta?.sourceType ?? "";
  const sourceId = row?.meta?.sourceId ?? "";
  const sourceGroup = row?.meta?.sourceGroup ?? "";
  const rowKind = row?.meta?.rowKind ?? "";
  const width = row?.meta?.widthM ?? "";
  const height = row?.meta?.heightM ?? "";
  const area = row?.meta?.areaM2 ?? "";
  const roofType = row?.meta?.roofType ?? "";
  const unit = row?.unit ?? "";
  const qty = getRowQuantity(row);
  const hours = row?.hours ?? "";

  return `${label}|${sourceType}|${sourceId}|${sourceGroup}|${rowKind}|${width}|${height}|${area}|${roofType}|${unit}|${qty}|${hours}`;
}